import React, { useState, useEffect } from 'react';
import { Table, TableHeader, TableBody, TableRow, TableCell } from "../../../components/ui/table";
import Badge from "../../../components/ui/badge/Badge";
import Button from "../../../components/ui/button/Button";
import ComponentCard from "../../../components/common/ComponentCard";
import { Eye, FileText, Search, Filter, X, Calendar, User, DollarSign, Plus, Check, AlertCircle, Info, ClipboardList, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import Select from "../../../components/form/Select";
import Input from "../../../components/form/input/InputField";
import { Modal } from "../../../components/ui/modal";
import useApi, { Ingreso } from '../../../hooks/useApi';
import { toast } from 'react-hot-toast';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import CreateIngresoModal from './CreateIngresoModal';
import useAuth from "../../../hooks/useAuth";

interface IngresosListProps {
  ingresos: Ingreso[];
  onDataChange?: () => Promise<void>;
}

interface Filters {
  estado: string;
  fechaInicio: string;
  fechaFin: string;
  cliente: string;
  montoMin: string;
  montoMax: string;
  metodoPago: string;
}

// Helper function to determine badge color based on estado
const getEstadoClasses = (estado: string): string => {
  if (estado === 'completo') return 'bg-green-100 text-green-800 dark:bg-green-800/20 dark:text-green-500';
  if (estado === 'parcial') return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800/20 dark:text-yellow-500';
  return 'bg-gray-100 text-gray-800 dark:bg-gray-800/20 dark:text-gray-500';
};

const IngresosList = ({ ingresos, onDataChange }: IngresosListProps): JSX.Element => {
  const { formatCurrency, formatMetodoPago, deleteIngreso } = useApi();
  const { canDelete, canCreate } = useAuth();
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedIngreso, setSelectedIngreso] = useState<Ingreso | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [ingresosState, setIngresos] = useState<Ingreso[]>([]);
  const [filters, setFilters] = useState<Filters>({
    estado: '',
    fechaInicio: '',
    fechaFin: '',
    cliente: '',
    montoMin: '',
    montoMax: '',
    metodoPago: ''
  });
  
  // Add pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Reset to first page when filters or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm]);

  useEffect(() => {
    if (ingresos) {
      setIngresos(ingresos);
    }
  }, [ingresos]);

  const handleViewDetails = (ingreso: Ingreso) => {
    setSelectedIngreso(ingreso);
    setIsDetailsModalOpen(true);
  };

  const handleDeleteConfirm = (ingreso: Ingreso) => {
    setSelectedIngreso(ingreso);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteIngreso = async () => {
    if (!selectedIngreso) return;
    
    try {
      setIsDeleting(true);
      await deleteIngreso(selectedIngreso.id);
      toast.success(
        `Ingreso eliminado exitosamente: ${formatMoneyValue(selectedIngreso.monto)} - ${selectedIngreso.clienteNombre}`,
        {
          duration: 4000,
          icon: <Check className="w-4 h-4 text-green-500" />,
        }
      );
      setIsDeleteModalOpen(false);
      
      // Refresh data
      if (onDataChange) {
        await onDataChange();
      }
    } catch (error) {
      console.error('Error al eliminar ingreso:', error);
      toast.error(
        'No se pudo eliminar el ingreso. Por favor, inténtelo de nuevo.',
        {
          duration: 5000,
          icon: <AlertCircle className="w-4 h-4 text-red-500" />,
        }
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterChange = (value: string, name: string) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({
      estado: '',
      fechaInicio: '',
      fechaFin: '',
      cliente: '',
      montoMin: '',
      montoMax: '',
      metodoPago: ''
    });
    setSearchTerm('');
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const formatMoneyValue = (value: number | undefined | null): string => {
    if (value === undefined || value === null) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(value);
  };

  const filteredIngresos = ingresosState.filter(ingreso => {
    // Apply search term
    if (searchTerm && !ingreso.clienteNombre.toLowerCase().includes(searchTerm.toLowerCase()) && 
        !(ingreso.facturaNumero?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false)) {
      return false;
    }
    
    // Apply filters
    if (filters.estado && ingreso.estado !== filters.estado) {
      return false;
    }
    
    if (filters.metodoPago && ingreso.metodoPago !== filters.metodoPago) {
      return false;
    }
    
    if (filters.fechaInicio) {
      const startDate = new Date(filters.fechaInicio);
      const ingresoDate = new Date(ingreso.fecha);
      if (ingresoDate < startDate) {
        return false;
      }
    }
    
    if (filters.fechaFin) {
      const endDate = new Date(filters.fechaFin);
      endDate.setHours(23, 59, 59, 999); // End of day
      const ingresoDate = new Date(ingreso.fecha);
      if (ingresoDate > endDate) {
        return false;
      }
    }
    
    if (filters.cliente && !ingreso.clienteNombre.toLowerCase().includes(filters.cliente.toLowerCase())) {
      return false;
    }
    
    if (filters.montoMin && ingreso.monto < parseFloat(filters.montoMin)) {
      return false;
    }
    
    if (filters.montoMax && ingreso.monto > parseFloat(filters.montoMax)) {
      return false;
    }
    
    return true;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredIngresos.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const paginatedIngresos = filteredIngresos.slice(indexOfFirstItem, indexOfLastItem);

  // Pagination handlers
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <ComponentCard title="Registro de Ingresos" className="h-full">
      <div className="p-2 space-y-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <input
              type="text"
              placeholder="Buscar por cliente o número de factura..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="w-full rounded-lg border border-gray-300 pl-10 pr-4 py-2.5 text-sm text-gray-900 dark:text-white dark:border-gray-600 dark:bg-gray-800 dark:placeholder-gray-400 focus:ring-primary-500 focus:border-primary-500"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400 dark:text-gray-500" />
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={toggleFilters}
              startIcon={<Filter className="w-4 h-4" />}
            >
              Filtros
            </Button>
            {canCreate && (
              <Button
                variant="primary"
                size="sm"
                startIcon={<Plus className="w-4 h-4" />}
                onClick={() => setIsCreateModalOpen(true)}
              >
                Nuevo ingreso
              </Button>
            )}
          </div>
        </div>
      </div>

      {showFilters && (
        <div className="p-5 bg-gradient-to-br from-gray-50 to-white dark:from-gray-800/70 dark:to-gray-800/30 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5 pb-3 border-b border-gray-200 dark:border-gray-700">
            <div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Filter className="w-4 h-4 text-primary-500" />
                Filtros avanzados
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Filtra los ingresos según los criterios que necesites
              </p>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={clearFilters}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white"
            >
              <X className="w-4 h-4 mr-1.5" />
              Limpiar filtros
            </Button>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-6 gap-y-4">
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <FileText className="w-4 h-4 text-primary-500 inline-block mr-1.5" />
                Estado
              </label>
              <Select
                options={[
                  { value: '', label: 'Todos los estados' },
                  { value: 'completo', label: 'Completado' },
                  { value: 'parcial', label: 'Parcial' }
                ]}
                defaultValue={filters.estado}
                onChange={(value) => handleFilterChange(value, 'estado')}
                placeholder="Seleccionar estado"
                className="w-full"
              />
            </div>
            
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <Calendar className="w-4 h-4 text-primary-500 inline-block mr-1.5" />
                Desde
              </label>
              <Input
                type="date"
                value={filters.fechaInicio}
                onChange={(e) => handleFilterChange(e.target.value, 'fechaInicio')}
                onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.target.showPicker()}
                className="w-full"
              />
            </div>
            
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <Calendar className="w-4 h-4 text-primary-500 inline-block mr-1.5" />
                Hasta
              </label>
              <Input
                type="date"
                value={filters.fechaFin}
                onChange={(e) => handleFilterChange(e.target.value, 'fechaFin')}
                onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.target.showPicker()}
                className="w-full"
              />
            </div>
            
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <User className="w-4 h-4 text-primary-500 inline-block mr-1.5" />
                Cliente
              </label>
              <Input
                type="text"
                value={filters.cliente}
                onChange={(e) => handleFilterChange(e.target.value, 'cliente')}
                placeholder="Nombre del cliente"
                className="w-full"
              />
            </div>
            
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <DollarSign className="w-4 h-4 text-primary-500 inline-block mr-1.5" />
                Importe mínimo
              </label>
              <div className="relative">
                <Input
                  type="number"
                  value={filters.montoMin}
                  onChange={(e) => handleFilterChange(e.target.value, 'montoMin')}
                  placeholder="0.00"
                  className="w-full"
                />
              </div>
            </div>
            
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <DollarSign className="w-4 h-4 text-primary-500 inline-block mr-1.5" />
                Importe máximo
              </label>
              <div className="relative">
                <Input
                  type="number"
                  value={filters.montoMax}
                  onChange={(e) => handleFilterChange(e.target.value, 'montoMax')}
                  placeholder="0.00"
                  className="w-full"
                />
              </div>
            </div>
            
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <FileText className="w-4 h-4 text-primary-500 inline-block mr-1.5" />
                Método de Pago
              </label>
              <Select
                options={[
                  { value: '', label: 'Todos los métodos' },
                  { value: 'efectivo', label: 'Efectivo' },
                  { value: 'tarjeta', label: 'Tarjeta' },
                  { value: 'transferencia', label: 'Transferencia bancaria' },
                  { value: 'automatico', label: 'Registro automático' },
                  { value: 'cheque', label: 'Cheque' },
                  { value: 'deposito', label: 'Depósito bancario' }
                ]}
                defaultValue={filters.metodoPago}
                onChange={(value) => handleFilterChange(value, 'metodoPago')}
                placeholder="Seleccionar método"
                className="w-full"
              />
            </div>
          </div>
        </div>
      )}

<div className="scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent dark:scrollbar-track-gray-800/50 overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
        <Table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <TableHeader className="bg-gray-50 dark:bg-gray-800/50">
            <TableRow>
              <TableCell isHeader className="px-6 py-3.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Fecha
              </TableCell>
              <TableCell isHeader className="px-6 py-3.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Cliente
              </TableCell>
              <TableCell isHeader className="px-6 py-3.5 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Monto
              </TableCell>
              <TableCell isHeader className="px-6 py-3.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Estado
              </TableCell>
              <TableCell isHeader className="px-6 py-3.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Factura
              </TableCell>
              <TableCell isHeader className="px-6 py-3.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Método de Pago
              </TableCell>
              <TableCell isHeader className="px-6 py-3.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Acciones
              </TableCell>
            </TableRow>
          </TableHeader>
          <TableBody className="bg-white dark:bg-transparent divide-y divide-gray-200 dark:divide-gray-700">
            {paginatedIngresos.length > 0 ? (
              paginatedIngresos.map((ingreso) => (
                <TableRow 
                  key={ingreso.id} 
                  className="hover:bg-gray-50/50 dark:hover:bg-gray-800/50 transition-colors"
                >
                  <TableCell className="px-6 py-4 text-sm text-gray-900 dark:text-gray-300 whitespace-nowrap">
                    {format(new Date(ingreso.fecha), 'dd/MM/yyyy')}
                  </TableCell>
                  <TableCell className="px-6 py-4 text-sm text-gray-900 dark:text-gray-300">
                    <div className="flex items-center space-x-2">
                      <span className="truncate max-w-[150px]" title={ingreso.clienteNombre}>
                        {ingreso.clienteNombre}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="px-6 py-4 text-sm text-gray-900 dark:text-gray-300 text-right whitespace-nowrap font-medium">
                    {formatMoneyValue(ingreso.monto)}
                  </TableCell>
                  <TableCell className="px-6 py-4 text-sm">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getEstadoClasses(ingreso.estado)}`}>
                      {ingreso.estado === 'completo' ? 'Completado' : 'Parcial'}
                    </span>
                  </TableCell>
                  <TableCell className="px-6 py-4 text-sm text-gray-900 dark:text-gray-300 whitespace-nowrap">
                    {ingreso.facturaNumero 
                      ? ingreso.facturaNumero 
                      : (
                        <span className="text-gray-500 italic">
                          Ingreso manual
                        </span>
                      )
                    }
                  </TableCell>
                  <TableCell className="px-6 py-4 text-sm text-gray-900 dark:text-gray-300 whitespace-nowrap">
                    {formatMetodoPago(ingreso.metodoPago)}
                  </TableCell>
                  <TableCell className="px-6 py-4 text-sm">
                    <div className="flex space-x-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handleViewDetails(ingreso)}
                        aria-label="Ver detalles"
                        className="text-gray-500 hover:text-gray-700 hover:border-gray-500"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      {!ingreso.facturaNumero && canDelete && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleDeleteConfirm(ingreso)}
                          aria-label="Eliminar ingreso"
                          className="text-red-500 hover:text-red-700 hover:border-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                  {searchTerm || Object.values(filters).some(f => f !== '') ? (
                    <div>
                      <p>No se encontraron ingresos con los filtros aplicados.</p>
                    </div>
                  ) : (
                    <p>No hay ingresos registrados en el sistema.</p>
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination controls */}
      {filteredIngresos.length > itemsPerPage && (
        <div className="flex items-center justify-between px-4 py-3 sm:px-6 border-t border-gray-200 dark:border-gray-700 mt-4">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              className={`relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-md 
                ${currentPage === 1 
                  ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' 
                  : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'} 
                border border-gray-300 dark:border-gray-600`}
            >
              Anterior
            </button>
            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className={`ml-3 relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-md
                ${currentPage === totalPages 
                  ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' 
                  : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'} 
                border border-gray-300 dark:border-gray-600`}
            >
              Siguiente
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Mostrando <span className="font-medium">{Math.min((currentPage - 1) * itemsPerPage + 1, filteredIngresos.length)}</span> a{" "}
                <span className="font-medium">{Math.min(currentPage * itemsPerPage, filteredIngresos.length)}</span> de{" "}
                <span className="font-medium">{filteredIngresos.length}</span> ingresos
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 text-sm font-medium
                    ${currentPage === 1 
                      ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' 
                      : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                >
                  <span className="sr-only">Anterior</span>
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
                
                {/* Page numbers - limited to 5 pages */}
                {(() => {
                  // Calculate which page numbers to show (max 5)
                  let startPage = Math.max(1, currentPage - 2);
                  let endPage = Math.min(totalPages, startPage + 4);
                  
                  // Adjust startPage if we're near the end
                  if (endPage - startPage < 4 && startPage > 1) {
                    startPage = Math.max(1, endPage - 4);
                  }
                  
                  const pages = [];
                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <button
                        key={i}
                        onClick={() => handlePageChange(i)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium
                          ${currentPage === i
                            ? 'z-10 bg-indigo-50 dark:bg-indigo-900/30 border-indigo-500 dark:border-indigo-500 text-indigo-600 dark:text-indigo-400'
                            : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                      >
                        {i}
                      </button>
                    );
                  }
                  return pages;
                })()}
                
                <button
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                  className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 text-sm font-medium
                    ${currentPage === totalPages 
                      ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' 
                      : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                >
                  <span className="sr-only">Siguiente</span>
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {selectedIngreso && (
        <Modal
          isOpen={isDetailsModalOpen}
          onClose={() => setIsDetailsModalOpen(false)}
          title={`Ingreso #${selectedIngreso.id}`}
        >
          <div className="p-5 max-h-[80vh] overflow-y-auto">
            {/* Header with amount and date */}
            <div className="mb-6 bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-lg p-5 border border-blue-200 dark:border-blue-800">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {formatMoneyValue(selectedIngreso.monto)}
                  </h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    {format(new Date(selectedIngreso.fecha), 'dd MMMM yyyy', { locale: es })}
                  </p>
                </div>
                <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${getEstadoClasses(selectedIngreso.estado)}`}>
                  {selectedIngreso.estado === 'completo' ? 'Completado' : 'Parcial'}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
              {/* Cliente information */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
                <div className="bg-gray-50 dark:bg-gray-800/50 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-white flex items-center">
                    <User className="w-4 h-4 mr-2 text-primary-500" />
                    Cliente
                  </h3>
                </div>
                <div className="p-4">
                  <div className="mb-3">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Nombre</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {selectedIngreso.clienteNombre}
                    </p>
                  </div>
                  
                  {selectedIngreso.facturaNumero && (
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Factura asociada</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {selectedIngreso.facturaNumero}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Transaction information */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
                <div className="bg-gray-50 dark:bg-gray-800/50 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-white flex items-center">
                    <FileText className="w-4 h-4 mr-2 text-primary-500" />
                    Detalles del Pago
                  </h3>
                </div>
                <div className="p-4">
                  <div className="mb-3">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Método de pago</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {formatMetodoPago(selectedIngreso.metodoPago)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Tipo de ingreso</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {selectedIngreso.facturaNumero ? (
                        <span className="text-green-600 dark:text-green-400">
                          Asociado a factura
                        </span>
                      ) : (
                        <span className="text-blue-600 dark:text-blue-400">
                          Ingreso manual
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Details */}
            {(selectedIngreso as any).descripcion && (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm mb-6">
                <div className="bg-gray-50 dark:bg-gray-800/50 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-white flex items-center">
                    <ClipboardList className="w-4 h-4 mr-2 text-primary-500" />
                    Descripción
                  </h3>
                </div>
                <div className="p-4">
                  <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                    {(selectedIngreso as any).descripcion}
                  </p>
                </div>
              </div>
            )}

            {/* Notes section, only if notes exist */}
            {(selectedIngreso as any).notas && (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm mb-6">
                <div className="bg-gray-50 dark:bg-gray-800/50 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-white flex items-center">
                    <FileText className="w-4 h-4 mr-2 text-primary-500" />
                    Notas
                  </h3>
                </div>
                <div className="p-4">
                  <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                    {(selectedIngreso as any).notas}
                  </p>
                </div>
              </div>
            )}

            {/* Metadata section with ID and timestamps */}
            <div className="bg-gray-50 dark:bg-gray-800/30 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <div className="grid grid-cols-2 gap-4 text-xs text-gray-500 dark:text-gray-400">
                <div>
                  <p>ID del Ingreso</p>
                  <p className="font-medium text-gray-700 dark:text-gray-300">{selectedIngreso.id}</p>
                </div>
                {(selectedIngreso as any).createdAt && (
                  <div>
                    <p>Fecha de registro</p>
                    <p className="font-medium text-gray-700 dark:text-gray-300">
                      {format(new Date((selectedIngreso as any).createdAt), 'dd/MM/yyyy HH:mm', { locale: es })}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Modal>
      )}

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedIngreso(null);
        }}
        title="Confirmación de eliminación"
        size="md"
      >
        <div className="space-y-6 px-2">
          <div className="space-y-4">
            <p className="text-lg font-medium text-gray-900 dark:text-gray-100">
              ¿Está seguro de eliminar este ingreso?
            </p>
            
            <div className="space-y-3">
              <p className="text-sm font-medium text-red-600 dark:text-red-400">
                Esta acción eliminará permanentemente todos los datos asociados al ingreso, incluyendo:
              </p>
              
              <ul className="list-disc list-inside text-sm text-gray-700 dark:text-gray-300 ml-4 space-y-1">
                <li>Información del ingreso</li>
                <li>Registros de transacciones</li>
                <li>Historial financiero relacionado</li>
                <li>Cualquier otro dato asociado</li>
              </ul>
            </div>

            <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/20 rounded-lg">
              <p className="text-sm font-medium text-red-600 dark:text-red-400">
                Esta acción no se puede deshacer.
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteModalOpen(false);
                setSelectedIngreso(null);
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={async () => {
                if (selectedIngreso !== null) {
                  try {
                    setIsDeleting(true);
                    await deleteIngreso(selectedIngreso.id);
                    toast.success("Ingreso eliminado exitosamente");
                    setIsDeleteModalOpen(false);
                    setSelectedIngreso(null);
                    
                    // Refresh data
                    if (onDataChange) {
                      await onDataChange();
                    }
                  } catch (error) {
                    toast.error("Error al eliminar el ingreso");
                  } finally {
                    setIsDeleting(false);
                  }
                }
              }}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {isDeleting ? 'Eliminando...' : 'Sí, eliminar ingreso'}
            </Button>
          </div>
        </div>
      </Modal>

      <CreateIngresoModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={onDataChange || (() => Promise.resolve())}
      />
    </ComponentCard>
  );
};

export default IngresosList;
